import { Extension } from '@tiptap/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import type { EditorState, Transaction } from '@tiptap/pm/state';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    blockIndent: {
      /** Shifts the selected top-level blocks (a whole list, a paragraph, a heading, a quote) one step right. */
      indentBlock: () => ReturnType;
      /** Shifts them one step back left. */
      outdentBlock: () => ReturnType;
    };
  }
}

const INDENTABLE = ['paragraph', 'heading', 'bulletList', 'orderedList', 'blockquote'];
export const MAX_BLOCK_INDENT = 8;
/** One step, in em of the block's own text. */
const STEP_EM = 2;

/** The top-level blocks the selection touches, with their positions. */
function selectedBlocks(state: EditorState): Array<{ node: ProseMirrorNode; pos: number }> {
  const blocks: Array<{ node: ProseMirrorNode; pos: number }> = [];
  const { from, to } = state.selection;
  state.doc.nodesBetween(from, to, (node, pos, parent) => {
    if (parent !== state.doc) return true;
    if (INDENTABLE.includes(node.type.name)) blocks.push({ node, pos });
    return false;
  });
  return blocks;
}

/** Whether any selected block can move; with `tr` (a real run, not `can()`), moves them. */
function shift(state: EditorState, delta: 1 | -1, tr?: Transaction): boolean {
  const changes = selectedBlocks(state)
    .map(block => ({ ...block, indent: Math.max(0, Math.min(MAX_BLOCK_INDENT, Number(block.node.attrs.indent ?? 0) + delta)) }))
    .filter(block => block.indent !== Number(block.node.attrs.indent ?? 0));
  changes.forEach(block => tr?.setNodeMarkup(block.pos, undefined, { ...block.node.attrs, indent: block.indent }));
  return changes.length > 0;
}

/**
 * Block indent (2026-10-27): a step-based left indent on whole top-level blocks, so a list can move right even when
 * its first item can't nest (ProseMirror's sinkListItem has no item to nest the first one under, and a whole-list
 * selection includes the first item), and so a paragraph can indent. It is stored as `data-indent` with an inline
 * margin, part of `richTextExtensions`, so every renderer (RichTextContent, richTextBlocks print) keeps it.
 * In a list, Tab nests the item when it can and otherwise shifts the whole list; Shift+Tab undoes the shift first,
 * then lifts. Outside lists Tab still leaves the editor, so the keyboard is never trapped.
 */
export const BlockIndent = Extension.create({
  name: 'blockIndent',
  // Ahead of ListItem's own Tab / Shift-Tab bindings.
  priority: 200,
  addGlobalAttributes() {
    return [{
      types: INDENTABLE,
      attributes: {
        indent: {
          default: 0,
          parseHTML: (element: HTMLElement) => {
            const value = Number(element.getAttribute('data-indent'));
            return Number.isFinite(value) ? Math.max(0, Math.min(MAX_BLOCK_INDENT, Math.round(value))) : 0;
          },
          renderHTML: (attributes: { indent?: number }) => attributes.indent
            ? { 'data-indent': String(attributes.indent), style: `margin-left: ${attributes.indent * STEP_EM}em` }
            : {},
        },
      },
    }];
  },
  addCommands() {
    return {
      indentBlock: () => ({ state, tr, dispatch }) => shift(state, 1, dispatch ? tr : undefined),
      outdentBlock: () => ({ state, tr, dispatch }) => shift(state, -1, dispatch ? tr : undefined),
    };
  },
  addKeyboardShortcuts() {
    const inList = () => this.editor.isActive('bulletList') || this.editor.isActive('orderedList');
    return {
      Tab: () => inList() && (this.editor.commands.sinkListItem('listItem') || this.editor.commands.indentBlock()),
      'Shift-Tab': () => inList() && (this.editor.commands.outdentBlock() || this.editor.commands.liftListItem('listItem')),
    };
  },
});
